import React, { useEffect, useRef, useState } from 'react';
import { useToastStore, Toast } from '../../store/toastStore';
import { useMessageStore } from '../../store/messageStore';
import { useServerStore } from '../../store/serverStore';
import { MessageSquare, UserPlus, AtSign, Bell, X } from 'lucide-react';

const ICON_MAP: Record<string, React.ElementType> = {
  message: MessageSquare,
  dm: MessageSquare,
  mention: AtSign,
  friend_request: UserPlus,
  system: Bell,
};

const ToastItem: React.FC<{ toast: Toast }> = ({ toast }) => {
  const removeToast = useToastStore((s) => s.removeToast);
  const { setCurrentDmUser } = useMessageStore();
  const { setCurrentChannel, setCurrentServer } = useServerStore();

  const [progress, setProgress] = useState(100);
  const [dismissed, setDismissed] = useState(false);
  const [translateX, setTranslateX] = useState(0);
  const startXRef = useRef<number | null>(null);
  const frameRef = useRef<number>(0);
  const startTimeRef = useRef(Date.now());

  // Countdown progress bar
  useEffect(() => {
    startTimeRef.current = Date.now();
    const tick = () => {
      const elapsed = Date.now() - startTimeRef.current;
      const remaining = Math.max(0, 100 - (elapsed / toast.duration) * 100);
      setProgress(remaining);
      if (remaining > 0) {
        frameRef.current = requestAnimationFrame(tick);
      } else {
        setDismissed(true);
        setTimeout(() => removeToast(toast.id), 300);
      }
    };
    frameRef.current = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(frameRef.current);
  }, [toast.id, toast.duration, removeToast]);

  // Swipe handling
  const handlePointerDown = (e: React.PointerEvent) => {
    startXRef.current = e.clientX;
    (e.target as HTMLElement).setPointerCapture(e.pointerId);
  };

  const handlePointerMove = (e: React.PointerEvent) => {
    if (startXRef.current === null) return;
    const dx = e.clientX - startXRef.current;
    // Only allow swipe left
    if (dx < 0) {
      setTranslateX(dx);
    }
  };

  const handlePointerUp = () => {
    if (translateX < -80) {
      setDismissed(true);
      setTranslateX(-400);
      setTimeout(() => removeToast(toast.id), 300);
    } else {
      setTranslateX(0);
    }
    startXRef.current = null;
  };

  const handleClick = () => {
    if (Math.abs(translateX) > 5) return; // ignore swipe clicks
    if (toast.action) {
      if (toast.action.type === 'dm' && toast.action.userId) {
        setCurrentServer(null);
        setCurrentChannel(null);
        setCurrentDmUser(toast.action.userId);
      } else if (toast.action.type === 'channel' && toast.action.channelId) {
        setCurrentDmUser(null);
        // Channel navigation: set server + channel
        if (toast.action.serverId) {
          const serverStore = useServerStore.getState();
          const server = serverStore.servers.find((s: any) => s.id === toast.action!.serverId);
          if (server) {
            setCurrentServer(server);
            setCurrentChannel({ id: toast.action.channelId, name: '', type: 'text' } as any);
          }
        }
      }
    }
    removeToast(toast.id);
  };

  const Icon = ICON_MAP[toast.type] || Bell;

  return (
    <div
      className={`relative w-80 overflow-hidden rounded-xl border border-border-color bg-bg-primary/95 backdrop-blur-md shadow-2xl shadow-black/40 cursor-pointer select-none transition-all duration-300 ${
        dismissed ? 'opacity-0 -translate-x-full' : 'opacity-100 animate-slide-in-right'
      }`}
      style={{ transform: dismissed ? undefined : `translateX(${translateX}px)` }}
      onPointerDown={handlePointerDown}
      onPointerMove={handlePointerMove}
      onPointerUp={handlePointerUp}
      onClick={handleClick}
    >
      <div className="flex items-start gap-3 p-3">
        {/* Avatar or icon */}
        {toast.avatarUrl ? (
          <img
            src={toast.avatarUrl}
            alt=""
            className="w-10 h-10 rounded-lg object-cover flex-shrink-0 border border-border-color"
          />
        ) : (
          <div className="w-10 h-10 rounded-lg bg-accent-color/10 border border-accent-color/20 flex items-center justify-center flex-shrink-0">
            <Icon className="w-5 h-5 text-accent-color" />
          </div>
        )}

        <div className="flex-1 min-w-0">
          <p className="text-xs font-semibold text-text-primary truncate">{toast.title}</p>
          <p className="text-[11px] text-text-secondary mt-0.5 line-clamp-2 leading-relaxed">
            {toast.body}
          </p>
        </div>

        {/* Close button */}
        <button
          onClick={(e) => {
            e.stopPropagation();
            setDismissed(true);
            setTimeout(() => removeToast(toast.id), 300);
          }}
          className="p-0.5 rounded hover:bg-highlight/50 text-text-muted hover:text-text-primary transition-all flex-shrink-0"
        >
          <X className="w-3.5 h-3.5" />
        </button>
      </div>

      {/* Progress bar */}
      <div className="h-[2px] bg-bg-tertiary">
        <div
          className="h-full bg-accent-color transition-none"
          style={{ width: `${progress}%` }}
        />
      </div>
    </div>
  );
};

const ToastContainer: React.FC = () => {
  const toasts = useToastStore((s) => s.toasts);

  if (toasts.length === 0) return null;

  return (
    <div className="fixed top-4 right-4 z-[9999] flex flex-col gap-2 pointer-events-auto">
      {toasts.map((toast) => (
        <ToastItem key={toast.id} toast={toast} />
      ))}
    </div>
  );
};

export default ToastContainer;
